import {
  getLocalTimeZone,
  now,
  parseDate,
  toCalendarDate,
} from "@internationalized/date";
import { useStore } from "@tanstack/react-form";
import {
  CopyIcon,
  PencilIcon,
  PlusIcon,
  RefreshCcwDotIcon,
  RefrigeratorIcon,
  XIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import type { z } from "zod";

import { newSampleSchema } from "@blis/shared/schemas/accession";
import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button, ButtonContext } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Modal } from "@/components/ui/modal";
import { Text } from "@/components/ui/text";
import { useCacheValues } from "@/lib/cache";
import type { TTubeWithFields } from "@/lib/types";
import { format } from "@/lib/utils";
import {
  getOffsetInfo,
  OffsetBadge,
  parseZonedDateTime,
  transformZonedDateTime,
} from "../detail/SampleSection";
import { type PreferredTube, useAccessionForm } from "./AccessionFormProvider";
import {
  SampleFormCustom,
  type SampleFormValues,
} from "./components/SampleFormCustom";
import { FormSection } from "./layout";
import { toDateValue } from "./schema";

type SampleFormValue = z.infer<typeof newSampleSchema>;

export function SamplesSection() {
  const { form } = useAccessionForm();

  const onNewSample = (sample: SampleFormValue) => {
    const current = form.getFieldValue("samples") ?? [];
    form.setFieldValue("samples", [...current, sample]);
    form.validateField("samples", "change");
  };

  return (
    <form.Field name="samples">
      {(field) => (
        <FormSection
          title="Samples"
          description="Samples can be managed here. These will be auto-selected from the above test order selections."
          error={field.state.meta.errors.join(", ")}
          actions={
            <div className="flex items-center gap-2">
              <SampleFormDialog onSubmit={onNewSample}>
                <Button type="button" variant="outline">
                  <PlusIcon />
                  Add sample
                </Button>
              </SampleFormDialog>
            </div>
          }
        >
          <SampleList />
        </FormSection>
      )}
    </form.Field>
  );
}

function SampleList() {
  const {
    form,
    preferredTubes,
    ignoredPreferredTubeIds,
    setIgnoredPreferredTubeIds,
  } = useAccessionForm();
  const samples = useStore(form.store, (s) => s.values.samples);

  const { data: tubesWithFields } = trpc.samples.tubesWithFields.useQuery(
    undefined,
    { staleTime: 1000 * 60 * 5 },
  );

  const filteredPreferredTubes = preferredTubes.filter((tube) => {
    return (
      !samples?.some((s) => s.tubeId === tube.id) &&
      !ignoredPreferredTubeIds.includes(tube.id)
    );
  });

  return (
    <AnimatePresence>
      {samples?.map((sample, idx) => (
        <motion.div
          key={`${sample.tubeId}-${sample.collection}-${idx}`}
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          className="bg-background overflow-hidden last:rounded-b-md"
        >
          <SampleListItem
            key={`sample-${sample.tubeId}-${idx}`}
            sample={sample as SampleFormValue}
            idx={idx}
            tubesWithFields={tubesWithFields}
          />
        </motion.div>
      ))}
      {filteredPreferredTubes.map((tube) => (
        <motion.div
          key={tube.id}
          initial={{ height: 0 }}
          animate={{ height: "auto" }}
          exit={{ height: 0 }}
          className="overflow-hidden bg-background rounded-b-md"
        >
          <div
            className="flex items-center gap-2 py-2 px-4 justify-between"
            key={tube.id}
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <Badge>1</Badge>
              <Text muted>{tube.name}</Text>
            </div>
            <div className="flex items-center gap-2">
              <Text muted size="xs" className="hidden md:inline">
                Preferred container. Click to add
              </Text>
              <SampleFormDialog
                onSubmit={(sample) => {
                  const current = form.getFieldValue("samples") ?? [];
                  form.setFieldValue("samples", [...current, sample]);
                  form.validateField("samples", "change");
                }}
                preferredTube={tube}
              >
                <Button type="button" variant="outline" size="icon-sm">
                  <PlusIcon />
                </Button>
              </SampleFormDialog>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onPress={() => {
                  setIgnoredPreferredTubeIds((prev) => [...prev, tube.id]);
                }}
              >
                <XIcon />
              </Button>
            </div>
          </div>
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

function SampleListItem({
  sample,
  idx,
  tubesWithFields,
}: {
  sample: SampleFormValue;
  idx: number;
  tubesWithFields?: TTubeWithFields[];
}) {
  const { tubeById } = useCacheValues();
  const tube = tubeById(sample.tubeId);

  // Use custom hooks for cleaner logic
  const { updateSample, duplicateSample, deleteSample } =
    useSampleOperations(idx);
  const { fieldsToDisplay, getCustomFieldDisplay } = useCustomFieldDisplay(
    sample,
    tubesWithFields,
  );

  // Create a duplicate sample with all built-in fields copied
  const getDuplicateSample = () => {
    return sample;
  };

  const isExpired =
    sample.expiration &&
    parseDate(sample.expiration).compare(now(getLocalTimeZone())) < 0;

  return (
    <div className="py-2 px-4">
      <div className="flex items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <QuantityBadge sample={sample} />
          <Text className="text-xs md:text-sm" truncate>
            {tube?.name}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          {isExpired ? (
            <Badge color="red">Expired</Badge>
          ) : (
            <>
              <RefrigeratedBadge sample={sample} />
              <CentrifugedBadge sample={sample} />
            </>
          )}
          <Text muted size="xs" mono className="hidden md:inline">
            {format(sample.collection)}
          </Text>
          <div>
            <SampleFormDialog
              sample={getDuplicateSample()}
              onSubmit={duplicateSample}
              title="Duplicate sample"
            >
              <Button type="button" variant="ghost" size="icon-sm">
                <CopyIcon />
              </Button>
            </SampleFormDialog>
            <SampleFormDialog sample={sample} onSubmit={updateSample}>
              <Button type="button" variant="ghost" size="icon-sm">
                <PencilIcon />
              </Button>
            </SampleFormDialog>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onPress={() => deleteSample(sample.tubeId)}
            >
              <XIcon />
            </Button>
          </div>
        </div>
      </div>
      {/* Custom fields display */}
      {fieldsToDisplay.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {fieldsToDisplay.map((field) => {
            if (!field.fieldSpec) return null;
            const displayValue = getCustomFieldDisplay(field.fieldSpec.id);
            if (!displayValue) return null;
            return (
              <Badge
                key={field.fieldSpec.id}
                variant="soft"
                className="text-xs"
              >
                {field.fieldSpec.name}: {displayValue}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Helper hook for getting defaults from last sample
function useGetDefaultsFromLastSample() {
  const { form } = useAccessionForm();

  return React.useCallback(() => {
    const currentValues = form.getFieldValue("samples");
    if (!currentValues?.length) return undefined;

    // Find the most recent custom sample
    const lastCustomSample = [...currentValues].reverse()[0];

    if (!lastCustomSample) return undefined;

    // Extract the fields we want to copy and create a complete SampleFormValues object
    const defaults: SampleFormValues = {
      tubeId: Number.NaN, // Reset so user must select
      fields: [],
    };

    if (lastCustomSample.collection) {
      const collection = parseZonedDateTime(
        lastCustomSample.collection,
        lastCustomSample.collectionTimezone || getLocalTimeZone(),
      );
      defaults.collection = collection;
    }

    if (lastCustomSample.refrigerated) {
      const refrigerated = parseZonedDateTime(
        lastCustomSample.refrigerated,
        lastCustomSample.refrigeratedTimezone || getLocalTimeZone(),
      );
      defaults.refrigerated = refrigerated;
    }

    if (lastCustomSample.centrifuged) {
      const centrifuged = parseZonedDateTime(
        lastCustomSample.centrifuged,
        lastCustomSample.centrifugedTimezone || getLocalTimeZone(),
      );
      defaults.centrifuged = centrifuged;
    }

    if (lastCustomSample.typeId) {
      defaults.typeId = lastCustomSample.typeId;
    }

    if (lastCustomSample.transfusionId) {
      defaults.transfusionId = lastCustomSample.transfusionId;
    }

    return defaults;
  }, [form]);
}

function SampleFormDialog({
  sample,
  onSubmit,
  children,
  preferredTube,
  title,
}: {
  sample?: SampleFormValue;
  onSubmit: (sample: SampleFormValue) => void;
  children: React.ReactNode;
  preferredTube?: PreferredTube;
  title?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const getDefaultsFromLastSample = useGetDefaultsFromLastSample();

  const handleSampleSubmit = ({ fields, ...sample }: SampleFormValues) => {
    const collection = transformZonedDateTime(sample.collection);
    const refrigerated = transformZonedDateTime(sample.refrigerated);
    const centrifuged = transformZonedDateTime(sample.centrifuged);

    const parsed = newSampleSchema.safeParse({
      ...sample,
      collection: collection.date,
      collectionTimezone: collection.timezone,
      refrigerated: refrigerated.date,
      refrigeratedTimezone: refrigerated.timezone,
      centrifuged: centrifuged.date,
      centrifugedTimezone: centrifuged.timezone,
      expiration: sample.expiration?.toString(),
    });

    if (!parsed.success) {
      console.error(parsed.error);
      return;
    }

    onSubmit(parsed.data);
    setOpen(false);
  };

  return (
    <DialogTrigger>
      <ButtonContext.Provider
        value={{ onPress: () => setOpen(true), "aria-expanded": open }}
      >
        {children}
      </ButtonContext.Provider>
      <Modal isOpen={open} onOpenChange={setOpen}>
        <Dialog>
          <DialogHeader>
            <DialogTitle>
              {title ?? `${sample ? "Edit" : "Add"} sample`}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <SampleFormCustom
              onSubmit={handleSampleSubmit}
              hideTimeZone
              sample={
                sample
                  ? {
                      ...sample,
                      collection: sample.collection
                        ? parseZonedDateTime(
                            sample.collection,
                            sample.collectionTimezone || getLocalTimeZone(),
                          )
                        : undefined,
                      refrigerated: sample.refrigerated
                        ? parseZonedDateTime(
                            sample.refrigerated,
                            sample.refrigeratedTimezone || getLocalTimeZone(),
                          )
                        : undefined,
                      centrifuged: sample.centrifuged
                        ? parseZonedDateTime(
                            sample.centrifuged,
                            sample.centrifugedTimezone || getLocalTimeZone(),
                          )
                        : undefined,
                      expiration: sample.expiration
                        ? // biome-ignore lint/style/noNonNullAssertion: it's ok
                          toCalendarDate(toDateValue(sample.expiration)!)
                        : undefined,
                    }
                  : getDefaultsFromLastSample()
              }
              preferredTube={preferredTube}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="submit" form="samples-form">
              Save
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function QuantityBadge({ sample }: { sample: SampleFormValue }) {
  if (!sample.quantity) {
    return null;
  }
  return <Badge>{sample.quantity}</Badge>;
}

function RefrigeratedBadge({ sample }: { sample: SampleFormValue }) {
  if (!sample.collection || !sample.refrigerated) {
    return null;
  }

  return (
    <OffsetBadge
      info={getOffsetInfo(sample.collection, sample.refrigerated)}
      verb="Refrigerated"
      icon={<RefrigeratorIcon className="size-3" />}
    />
  );
}

function CentrifugedBadge({ sample }: { sample: SampleFormValue }) {
  if (!sample.collection || !sample.centrifuged) {
    return null;
  }

  return (
    <OffsetBadge
      info={getOffsetInfo(sample.collection, sample.centrifuged)}
      verb="Centrifuged"
      icon={<RefreshCcwDotIcon className="size-3" />}
    />
  );
}

// Custom hook for sample operations
function useSampleOperations(idx: number) {
  const { form, setIgnoredPreferredTubeIds } = useAccessionForm();

  const updateSample = React.useCallback(
    (newSample: SampleFormValue) => {
      const samples = form.getFieldValue("samples") ?? [];
      form.setFieldValue(
        "samples",
        samples.map((s, i) => (i === idx ? newSample : s)),
      );
    },
    [form, idx],
  );

  const duplicateSample = React.useCallback(
    (newSample: SampleFormValue) => {
      const current = form.getFieldValue("samples") ?? [];
      form.setFieldValue("samples", [...current, newSample]);
      form.validateField("samples", "change");
    },
    [form],
  );

  const deleteSample = React.useCallback(
    (tubeId: number) => {
      setIgnoredPreferredTubeIds((prev) => [...prev, tubeId]);
      const samples = form.getFieldValue("samples") ?? [];
      form.setFieldValue(
        "samples",
        samples.filter((_, i) => i !== idx),
      );
    },
    [form, idx, setIgnoredPreferredTubeIds],
  );

  return { updateSample, duplicateSample, deleteSample };
}

// Custom hook for custom field display logic
function useCustomFieldDisplay(
  sample: SampleFormValue,
  tubesWithFields?: TTubeWithFields[],
) {
  const fieldsToDisplay = React.useMemo(() => {
    if (!tubesWithFields) return [];

    const tubeWithFields = tubesWithFields.find((t) => t.id === sample.tubeId);
    const fields = tubeWithFields?.fieldSet?.fields ?? [];

    return fields.filter(
      (f) => (f.fieldSpec?.displayOptions as Record<string, boolean>)?.form,
    );
  }, [tubesWithFields, sample]);

  const getCustomFieldDisplay = React.useCallback(
    (fieldId: number) => {
      const field = fieldsToDisplay.find((f) => f.fieldSpec?.id === fieldId);
      if (!field?.fieldSpec) return null;

      // Find the field value from the sample
      const fieldKey = `field-${fieldId}` as const;
      // biome-ignore lint/suspicious/noExplicitAny: it's ok
      const fieldValue = (sample as any)[fieldKey];

      if (!fieldValue) return null;

      const spec = field.fieldSpec;

      switch (spec.type) {
        case "TEXT":
          return fieldValue.textValue;
        case "NUMBER":
          return fieldValue.numericValue?.toString();
        case "DATE":
        case "DATETIME":
          return fieldValue.dateValue;
        case "SELECT":
          if (fieldValue.optionId) {
            // Resolve the foreign key to display value
            const option = spec.options.find(
              (o) => o.id === fieldValue.optionId,
            );
            return option?.value;
          }
          // Fallback to text value for free text selects
          return fieldValue.textValue;
        default:
          return null;
      }
    },
    [sample, fieldsToDisplay],
  );

  return { fieldsToDisplay, getCustomFieldDisplay };
}
